import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/http";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, object=logged in
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("pc_token");
    if (!token) { setUser(false); return; }
    // Only a real 401 means the session is gone. A deploy/restart, flaky Wi-Fi or a 5xx must not log the user out —
    // keep the token and retry until the server answers.
    let cancelled = false;
    const load = (attempt = 0) => api.get("/auth/me")
      .then(({ data }) => { if (!cancelled) { setUser(data.user); setBusiness(data.business); } })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 401) { localStorage.removeItem("pc_token"); setUser(false); return; }
        if (attempt < 6) setTimeout(() => load(attempt + 1), Math.min(2000 * 2 ** attempt, 15000));
        else setUser(false);
      });
    load();
    return () => { cancelled = true; };
  }, []);

  const applyAuth = (data) => {
    localStorage.setItem("pc_token", data.token);
    sessionStorage.removeItem("pc_locked");
    setUser(data.user);
    setBusiness(data.business);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyAuth(data);
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    applyAuth(data);
    return data;
  };

  const switchUser = async (token) => {
    localStorage.setItem("pc_token", token);
    const { data } = await api.get("/auth/me");
    setUser(data.user);
    setBusiness(data.business);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("pc_token");
    sessionStorage.removeItem("pc_locked");
    setUser(false);
    setBusiness(null);
  };

  const isAdmin = user && (user.role === "owner" || user.role === "admin");
  const isReception = user && user.role === "reception";
  const isStaff = user && user.role === "staff";
  const role = user ? user.role : null;
  const can = (key) => {
    if (!user) return false;
    if (user.role === "owner" || user.role === "admin") return true;
    return !!(user.effective_permissions || {})[key];
  };

  return (
    <AuthContext.Provider value={{ user, business, setBusiness, login, register, switchUser, logout, isAdmin, isReception, isStaff, role, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
